import React, { useEffect, useRef, useState } from 'react';
import { BranchesSectionProps } from '../../types';
import { typography, layout, colors, borderRadius } from '../../styles/designTokens';
import { useScrollAnimation } from '../../utils/useScrollAnimation';

// סוג לנתוני עיר עם סניפיה
interface CityBranches {
  city: string;
  branches: Array<{ store: string; address: string }>;
}

const BranchesSection: React.FC<BranchesSectionProps> = ({
  branches,
  pointSaleTitleIcon,
  pointSaleIcon,
}) => {
  const titleRef = useScrollAnimation({ animationType: 'scaleIn', threshold: 0.1, triggerOnce: true });
  const cardsRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [hoveredCity, setHoveredCity] = useState<string | null>(null);
  const [clickedCity, setClickedCity] = useState<string | null>(null); // ✅ state חדש למעקב אחרי כרטיס שנלחץ

  // קיבוץ סניפים לפי עיר
  const citiesData: CityBranches[] = React.useMemo(() => {
    const cityMap = new Map<string, Array<{ store: string; address: string }>>();
    
    branches.forEach(branch => {
      if (!cityMap.has(branch.city)) {
        cityMap.set(branch.city, []);
      }
      cityMap.get(branch.city)!.push({
        store: branch.store,
        address: branch.address,
      });
    });

    return Array.from(cityMap.entries()).map(([city, branches]) => ({
      city,
      branches,
    }));
  }, [branches]);

  // אנימציה לכל הכרטיסים עם IntersectionObserver
  useEffect(() => {
    const cards = cardsRef.current?.querySelectorAll('.branch-card');
    if (!cards || cards.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, index * 50); // delay לכל כרטיס
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cards.forEach(card => observer.observe(card));

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, [citiesData]);

  // בדיקה אם הסקשן במסך - לעצירת החלקיקים
  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  // Animated Particles Effect - חלקיקים זוהרים כתומים
  useEffect(() => {
    if (!particlesRef.current || !sectionRef.current) return;

    const container = particlesRef.current;
    const section = sectionRef.current;
    const particles: Array<{
      element: HTMLDivElement;
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
    }> = [];

    // יצירת חלקיקים
    const createParticles = () => {
      const particleCount = 30; // הוקטן מ-100 ל-30 - חיסכון של 70%!
      const sectionRect = section.getBoundingClientRect();

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        const size = Math.random() * 6 + 4; // 4-10px - הוגדל משמעותית
        const x = Math.random() * sectionRect.width;
        const y = Math.random() * sectionRect.height;

        particle.style.position = 'absolute';
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.borderRadius = '50%';
        particle.style.background = `radial-gradient(circle, 
          rgba(245, 182, 88, 1) 0%, 
          rgba(245, 182, 88, 0.9) 30%, 
          rgba(245, 182, 88, 0.6) 60%, 
          rgba(245, 182, 88, 0.3) 90%, 
          transparent 100%
        )`;
        particle.style.boxShadow = `0 0 ${size * 4}px rgba(245, 182, 88, 1), 
                                     0 0 ${size * 7}px rgba(245, 182, 88, 0.7),
                                     0 0 ${size * 10}px rgba(245, 182, 88, 0.4)`;
        particle.style.pointerEvents = 'none';
        particle.style.left = `${x}px`;
        particle.style.top = `${y}px`;
        particle.style.zIndex = '0';
        particle.style.opacity = String(Math.random() * 0.3 + 0.7); // 0.7-1.0 - הוגדל משמעותית

        container.appendChild(particle);

        particles.push({
          element: particle,
          x,
          y,
          vx: (Math.random() - 0.5) * 0.9, // מהירות אופקית - הוקטנה
          vy: (Math.random() - 0.5) * 0.9, // מהירות אנכית - הוקטנה
          size,
        });
      }
    };

    // אנימציה של החלקיקים
    let animationFrameId: number | null = null;
    let lastTime = performance.now();
    let lastUpdateTime = 0;
    const throttleDelay = 1000 / 10; // 20 FPS - חיסכון של 67%!

    const animate = (currentTime: number) => {
      // עצירה כשלא במסך - חיסכון באנרגיה
      if (!isVisible) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      // Throttle - רק כל 33ms (30 פעמים בשנייה)
      if (currentTime - lastUpdateTime < throttleDelay) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      lastUpdateTime = currentTime;

      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      const sectionRect = section.getBoundingClientRect();

      particles.forEach(particle => {
        // עדכון מיקום
        particle.x += particle.vx * (deltaTime * 0.1);
        particle.y += particle.vy * (deltaTime * 0.1);

        // גבולות - חלקיקים חוזרים לצד השני
        if (particle.x < 0) particle.x = sectionRect.width;
        if (particle.x > sectionRect.width) particle.x = 0;
        if (particle.y < 0) particle.y = sectionRect.height;
        if (particle.y > sectionRect.height) particle.y = 0;

        // עדכון מיקום DOM
        particle.element.style.left = `${particle.x}px`;
        particle.element.style.top = `${particle.y}px`;

        // שינוי בשקיפות (נשימה) - הוגדל להיות יותר בולט
        const opacity = 0.6 + Math.sin(currentTime * 0.001 + particle.x * 0.01) * 0.4;
        particle.element.style.opacity = String(Math.max(0.5, Math.min(1.0, opacity)));
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    createParticles();
    animationFrameId = requestAnimationFrame(animate);

    // טיפול בשינוי גודל החלון
    const handleResize = () => {
      const sectionRect = section.getBoundingClientRect();
      particles.forEach(particle => {
        particle.x = Math.min(particle.x, sectionRect.width);
        particle.y = Math.min(particle.y, sectionRect.height);
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      particles.forEach(particle => {
        if (particle.element.parentNode) {
          particle.element.parentNode.removeChild(particle.element);
        }
      });
    };
  }, [isVisible]);


  return (
    <section 
      ref={sectionRef}
      id="branches" 
      className="relative mt-[-80px] z-20 scrollbar-hide" 
      style={{ 
        paddingTop: 'clamp(40px, calc(2vw + 40px), 60px)',
        paddingBottom: 'clamp(20px, 5vw, 80px)',
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        overflow: 'visible', // מאפשר לכרטיסים לחרוג מהמכולה
        zIndex: 20,
      }}
    >
      {/* Background Layer - רקע שחור עם פינות מעוגלות */}
      <div 
        className="absolute inset-0 bg-black rounded-t-[50px] overflow-hidden -z-10"
        style={{
          backgroundColor: colors.background,
        }}
      />
      
      {/* Animated Particles Background - חלקיקים זוהרים כתומים */}
      <div
        ref={particlesRef}
        className="absolute inset-0"
        style={{
          pointerEvents: 'none',
          zIndex: 0,
          overflow: 'hidden',
        }}
      />
      
      <div className="container mx-auto px-4 relative scrollbar-hide" style={{ maxWidth: layout.contentMaxWidth, width: '100%', zIndex: 30, overflow: 'visible' }}>
        {/* Section Title */}
        <div ref={titleRef} className="flex items-center justify-center relative mb-12 branches-title-container" style={{ zIndex: 1 }}>
          <h2 
            className="branches-title font-black leading-tight relative z-0"
            style={{ 
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontFamily: typography.heading.fontFamily,
              letterSpacing: typography.heading.letterSpacing,
              background: colors.accentSpecial.textGradientRich,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            נקודות מכירה
          </h2>
        </div>

        {/* Grid Cards - כרטיסים של ערים */}
        <div 
          ref={cardsRef} 
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 scrollbar-hide" 
          style={{ 
            gap: 'clamp(20px, 3vw, 32px)', 
            justifyItems: 'stretch',
            overflow: 'visible', // מאפשר לכרטיסים לחרוג
            position: 'relative',
            zIndex: 40, // z-index גבוה יותר מהרקע
          }}
        >
          {citiesData.map((cityData, index) => {
            const colorScheme = {
              backgroundColor: '#1a1a1a',
              borderColor: colors.gold.base,
              textColor: 'gradient',
              textGradient: colors.accentSpecial.textGradientBright,
              iconFilter: 'brightness(0) invert(1)',
              boxShadow: `0 4px 20px rgba(212, 148, 58, 0.15)`,
              borderWidth: '2px',
            };
            
            const branchIndex = 0;
            const currentBranch = cityData.branches[branchIndex];
            
            const isHovered = hoveredCity === cityData.city;
            const isClicked = clickedCity === cityData.city; // ✅ בדיקה אם הכרטיס נלחץ
            const isOpen = isClicked; // ✅ הכרטיס פתוח רק אם נלחץ (לא hover)
            
            return (
              <div
                key={cityData.city}
                className="branch-card relative scroll-fade-in" // הוסר overflow-hidden כדי לאפשר לכרטיס להיות גבוה יותר עם הרבה סניפים
                style={{
                  backgroundColor: colorScheme.backgroundColor,
                  border: `${colorScheme.borderWidth || '2px'} solid ${colorScheme.borderColor}`,
                  borderRadius: borderRadius.soft,
                  padding: 'clamp(20px, 3vw, 28px)',
                  minHeight: isOpen ? 'auto' : 'clamp(140px, 18vw, 180px)', // ✅ שונה מ-isHovered ל-isOpen
                  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transitionDelay: `${index * 0.05}s`,
                  transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)', // ✅ hover עדיין עובד לאפקט ויזואלי
                  boxShadow: colorScheme.boxShadow,
                  zIndex: isOpen ? 50 : 20, // ✅ שונה מ-isHovered ל-isOpen
                  cursor: 'pointer',
                }}
                onClick={() => setClickedCity(clickedCity === cityData.city ? null : cityData.city)} // ✅ לחיצה פותחת/סוגרת את הכרטיס
                onMouseEnter={() => setHoveredCity(cityData.city)}
                onMouseLeave={() => setHoveredCity(null)}
              >
                {!isOpen ? ( // ✅ שונה מ-isHovered ל-isOpen
                  /* מצב רגיל - רק שם העיר */
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    {/* Location Pin Icon */}
                    <div className="mb-4 flex items-center justify-center">
                      <img
                        src={pointSaleIcon}
                        alt="סמל נקודת מכירה"
                        className="object-contain animate-float"
                        style={{
                          width: 'clamp(56px, 8vw, 64px)',
                          height: 'clamp(56px, 8vw, 64px)',
                          animationDelay: `${index * 0.1}s`,
                          filter: colorScheme.iconFilter,
                        }}
                        loading="lazy"
                      />
                    </div>

                    {/* City Name */}
                    <h3 
                      className="font-black"
                      style={{ 
                        fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
                        letterSpacing: typography.heading.letterSpacing,
                        background: colorScheme.textGradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        lineHeight: '1.3',
                      }}
                    >
                      {cityData.city}
                    </h3>

                    {/* קריאה לפעולה - "הצג סניפים" */}
                    <p 
                      className="mt-3 font-bold"
                      style={{ 
                        fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)', // ✅ גדול יותר מהקודם
                        color: colors.textMuted, // ✅ אפור במקום כתום - מרמז שצריך ללחוץ
                        opacity: 0.9,
                        letterSpacing: '0.5px',
                      }}
                    >
                      הצג סניפים
                    </p>
                  </div>
                ) : (
                  /* מצב hover - הצגת סניפים */
                  <div className="flex flex-col h-full">
                    {/* כותרת העיר למעלה */}
                    <div className="flex items-center justify-center mb-4 pb-3 border-b" style={{ borderColor: `${colorScheme.borderColor}30` }}>
                      <img
                        src={pointSaleIcon}
                        alt="סמל נקודת מכירה"
                        className="w-10 h-10 md:w-12 md:h-12 object-contain mr-2"
                        style={{
                          filter: colorScheme.iconFilter,
                        }}
                        loading="lazy"
                      />
                      <h3 
                        className="font-black"
                        style={{ 
                          fontSize: 'clamp(1rem, 1.8vw, 1.3rem)',
                          letterSpacing: typography.heading.letterSpacing,
                          background: colorScheme.textGradient,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        {cityData.city}
                      </h3>
                    </div>

                    {/* רשימת סניפים */}
                    <div className="flex-1 space-y-3">
                      {cityData.branches.map((branch, branchIndex) => (
                        <div
                          key={branchIndex}
                          className="p-3 rounded"
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            border: `1px solid ${colors.gold.mediumLight}`,
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${colors.gold.mediumLight}30`;
                            e.currentTarget.style.borderColor = colors.gold.mediumLight;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
                            e.currentTarget.style.borderColor = colors.gold.mediumLight;
                          }}
                        >
                          {/* Store Name */}
                          <p 
                            className="font-bold mb-1"
                            style={{ 
                              fontSize: 'clamp(0.9rem, 1.4vw, 1.1rem)',
                              letterSpacing: typography.body.letterSpacing,
                              background: colors.accentSpecial.textGradientRich,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {branch.store}
                          </p>

                          {/* Address */}
                          {branch.address && (
                            <p 
                              className="font-normal"
                              style={{ 
                                fontSize: 'clamp(0.8rem, 1.2vw, 0.95rem)',
                                letterSpacing: typography.body.letterSpacing,
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: '1.4',
                              }}
                            >
                              {branch.address}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BranchesSection;
